/*!
=========================================================
* Argon Dashboard PRO React - v1.2.0
=========================================================
* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)
* Coded by Creative Tim
=========================================================
* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
import React from "react";
// React-Redux
import { useDispatch, useSelector } from "react-redux";
// Moment
import moment from 'moment';
// JavaScript library that creates a callendar with events
import { Calendar } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import interaction from "@fullcalendar/interaction";
// reactstrap components
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Form,
  Input,
  Modal,
  Container,
  Row,
  Col,
} from "reactstrap";
// core components

import { getPastChecklists, createCalendarEvents, createUserCalendarEvents } from 'actions/checklistActions.js';

let calendar;

function CalendarView({ eventsVariables, locations, uid }) {
  const dispatch = useDispatch();
  const checklistsState = useSelector(state => state.checklists);
  const [alert, setAlert] = React.useState(null);
  const [modalAdd, setModalAdd] = React.useState(false);
  const [modalChange, setModalChange] = React.useState(false);
  const [startDate, setStartDate] = React.useState(null);
  const [endDate, setEndDate] = React.useState(null);
  const [radios, setRadios] = React.useState(null);
  const [eventId, setEventId] = React.useState(null);
  const [eventTitle, setEventTitle] = React.useState(null);
  const [eventDescription, setEventDescription] = React.useState(null);
  // eslint-disable-next-line
  const [event, setEvent] = React.useState(null);
  const [currentDate, setCurrentDate] = React.useState(null);
  const calendarRef = React.useRef(null);
  const isMounted = React.useRef(false);
  const [calendarEventsLength, setCalendarEventsLength] = React.useState(0);
  React.useEffect(() => {
    createCalendar();
    // eslint-disable-next-line
  }, []);
  const createCalendar = (events = null) => {
    calendar = new Calendar(calendarRef.current, {
      plugins: [interaction, dayGridPlugin],
      initialView: "dayGridMonth",
      selectable: true,
      editable: true,
      events: events ? events : eventsVariables,
      headerToolbar: "",
      // Add new event
      select: info => {
        setModalAdd(true);
        setStartDate(info.startStr);
        setEndDate(info.endStr);
        setRadios("bg-info");
      },
      // Edit calendar event action
      eventClick: ({ event }) => {
        setEventId(event.id);
        setEventTitle(event.title);
        setEventDescription(event.extendedProps.description);
        setRadios("bg-info");
        setEvent(event);
        setModalChange(true);
      },
    });
    calendar.render();
    setCurrentDate(calendar.view.title);
  };
  const changeView = newView => {
    calendar.changeView(newView);
    setCurrentDate(calendar.view.title);
  };

  React.useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    } else {
      if (checklistsState.calendar.length > calendarEventsLength) {
        createCalendar(checklistsState.calendar);
        calendar.prev();
        setCurrentDate(calendar.view.title);
        setCalendarEventsLength(checklistsState.calendar.length);
      } else {
        calendar.prev();
        setCurrentDate(calendar.view.title);
      }
    };
  }, [checklistsState.calendar])

  return (
    <>
      {alert}
      <div className="header header-dark bg-info mb-3 content__title content__title--calendar">
        <Container fluid>
          <div className="header-body">
            <Row className="align-items-center py-4">
              <Col className="mt-3 mt-md-0 text-md-right" lg="6">
                <h6 className="fullcalendar-title h2 text-white d-inline-block mb-0 mr-1">
                  {currentDate}
                </h6>
              </Col>
              <Col className="mt-3 mt-md-0 text-md-right" lg="6">
                <Button
                  className="fullcalendar-btn-prev btn-neutral"
                  color="default"
                  onClick={async () => {
                    if (locations) {
                      await Promise.all(locations.map(location => dispatch(
                        getPastChecklists(
                          moment(calendar.view.currentStart)
                            .clone()
                            .startOf("month")
                            .subtract(1, "month")
                            .format("YYYY-MM-DD"),
                          location.split('%')[0],
                          location.split('%')[1]
                        ))))
                      return dispatch(createUserCalendarEvents(uid));
                    }
                    await dispatch(
                      getPastChecklists(
                        moment(calendar.view.currentStart)
                          .clone()
                          .startOf("month")
                          .subtract(1, "month")
                          .format("YYYY-MM-DD")
                      )
                    );
                    return dispatch(createCalendarEvents(uid));
                  }}
                  size="sm"
                >
                  <i className="fas fa-angle-left" />
                </Button>
                <Button
                  className="fullcalendar-btn-next btn-neutral"
                  color="default"
                  onClick={() => {
                    calendar.next();
                    setCurrentDate(calendar.view.title);
                  }}
                  size="sm"
                >
                  <i className="fas fa-angle-right" />
                </Button>
                <Button
                  className="btn-neutral"
                  color="default"
                  data-calendar-view="month"
                  onClick={() => changeView("dayGridMonth")}
                  size="sm"
                >
                  Month
                </Button>
                <Button
                  className="btn-neutral"
                  color="default"
                  data-calendar-view="basicWeek"
                  onClick={() => changeView("dayGridWeek")}
                  size="sm"
                >
                  Week
                </Button>
                <Button
                  className="btn-neutral"
                  color="default"
                  data-calendar-view="basicDay"
                  onClick={() => changeView("dayGridDay")}
                  size="sm"
                >
                  Day
                </Button>
              </Col>
            </Row>
          </div>
        </Container>
      </div>
      <Container fluid>
        <Row>
          <div className="col">
            <Card className="card-calendar bg-default">
              <CardHeader className="bg-transparent">
                <h5 className="h3 mb-0 text-white">
                  Checklists Completion Calendar
                </h5>
              </CardHeader>
              <CardBody className="p-0">
                <div
                  className="calendar"
                  data-toggle="calendar"
                  id="calendar"
                  ref={calendarRef}
                />
              </CardBody>
            </Card>
            <Modal
              isOpen={modalChange}
              toggle={() => setModalChange(false)}
              className="modal-dialog-centered modal-secondary"
              style={{ width: "80%", maxWidth: "500px" }}
            >
              <div className="modal-body">
                <Form className="edit-event--form">
                  <FormGroup>
                    <label className="form-control-label">{window.location.pathname === "/admin/checklists-calendar" ? 'Completion Details' : 'Checklist Details'}</label>
                    <Input
                      className="form-control-alternative edit-event--title"
                      placeholder="Event Title"
                      type="text"
                      defaultValue={eventTitle}
                      onChange={e => setEventTitle(e.target.value)}
                      disabled
                    />
                  </FormGroup>
                  <FormGroup>
                    <label className="form-control-label">Description</label>
                    <Input
                      className="form-control-alternative edit-event--description textarea-autosize"
                      placeholder="Event Desctiption"
                      type="textarea"
                      style={{ height: "200px" }}
                      defaultValue={eventDescription}
                      onChange={e => setEventDescription(e.target.value)}
                      disabled
                    />
                    <i className="form-group--bar" />
                  </FormGroup>
                  <input className="edit-event--id" type="hidden" />
                </Form>
              </div>
              <div className="modal-footer">
                <Button
                  className="ml-auto"
                  color="secondary"
                  onClick={() => setModalChange(false)}
                >
                  Close
                </Button>
              </div>
            </Modal>
          </div>
        </Row>
      </Container>
    </>
  );
}

export default CalendarView;
